import { formatIcsDate, formatUtcStamp, nextCalendarDay } from './dates';

export type CalendarEventInput = {
  id: string;
  title: string;
  description: string;
  date: Date;
};

const CALENDAR_NAME = 'Utviklingsplan';

function escapeText(value: string): string {
  return value
    .replace(/\\/g, '\\\\')
    .replace(/\r\n|\n|\r/g, '\\n')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,');
}

function utf8Size(char: string): number {
  const code = char.codePointAt(0) ?? 0;
  if (code <= 0x7f) {
    return 1;
  }
  if (code <= 0x7ff) {
    return 2;
  }
  if (code <= 0xffff) {
    return 3;
  }
  return 4;
}

export function utf8ByteLength(value: string): number {
  let size = 0;
  for (const char of value) {
    size += utf8Size(char);
  }
  return size;
}

function foldLine(line: string): string {
  if (utf8ByteLength(line) <= 75) {
    return line;
  }

  const parts: string[] = [];
  let chunk = '';
  let chunkBytes = 0;
  let budget = 75;

  const flush = () => {
    parts.push(parts.length === 0 ? chunk : ` ${chunk}`);
    chunk = '';
    chunkBytes = 0;
    budget = 74;
  };

  for (const char of line) {
    const size = utf8Size(char);
    if (chunkBytes + size > budget && chunkBytes > 0) {
      flush();
    }
    chunk += char;
    chunkBytes += size;
  }
  if (chunk) {
    flush();
  }

  return parts.join('\r\n');
}

function eventUid(id: string): string {
  const safe = id.replace(/[^A-Za-z0-9-]/g, '-');
  return `devplan-${safe}@dunbar`;
}

function eventLines(event: CalendarEventInput, stamp: string): string[] {
  const end = nextCalendarDay(event.date);
  return [
    'BEGIN:VEVENT',
    `UID:${eventUid(event.id)}`,
    `DTSTAMP:${stamp}`,
    `DTSTART;VALUE=DATE:${formatIcsDate(event.date)}`,
    `DTEND;VALUE=DATE:${formatIcsDate(end)}`,
    'SEQUENCE:0',
    `SUMMARY:${escapeText(event.title)}`,
    `DESCRIPTION:${escapeText(event.description)}`,
    'TRANSP:TRANSPARENT',
    'BEGIN:VALARM',
    'TRIGGER;RELATED=START:PT9H',
    'ACTION:DISPLAY',
    `DESCRIPTION:${escapeText(event.title)}`,
    'END:VALARM',
    'END:VEVENT',
  ];
}

export function buildIcsCalendar(
  events: CalendarEventInput[],
  stampAt: Date
): string {
  const stamp = formatUtcStamp(stampAt);
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Dunbar//Utviklingsplan//NO',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    `X-WR-CALNAME:${escapeText(CALENDAR_NAME)}`,
    ...events.flatMap((event) => eventLines(event, stamp)),
    'END:VCALENDAR',
  ];
  return `${lines.map(foldLine).join('\r\n')}\r\n`;
}

export function buildGoogleCalendarUrl(event: CalendarEventInput): string {
  const end = nextCalendarDay(event.date);
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: event.title,
    dates: `${formatIcsDate(event.date)}/${formatIcsDate(end)}`,
    details: event.description,
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

export function calendarEventFromStep(
  step: { id: string; title: string; text: string; context: string },
  date: Date,
  planUrl: string
): CalendarEventInput {
  return {
    id: step.id,
    title: step.title,
    description: `${step.text}\n\n${step.context}\n\nÅpne utviklingsplanen: ${planUrl}`,
    date,
  };
}
